'use client';

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { newrelic } from '@hen-site/lib/newrelic/client';

interface DynamicExperienceErrorBoundaryProps {
  children: ReactNode;
  componentName?: string;
  tenantId?: string;
}

interface DynamicExperienceErrorBoundaryState {
  hasError: boolean;
}

export class DynamicExperienceErrorBoundary extends Component<
  DynamicExperienceErrorBoundaryProps,
  DynamicExperienceErrorBoundaryState
> {
  constructor(props: DynamicExperienceErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): DynamicExperienceErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    newrelic.noticeError(error, {
      source: 'DynamicExperienceErrorBoundary',
      componentName: this.props.componentName ?? 'unknown',
      tenantId: this.props.tenantId ?? 'unknown',
      componentStack: info.componentStack ?? '',
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className='rounded-lg border border-neutral-200 bg-neutral-50 px-6 py-8 text-center'
          role='alert'
        >
          <p className='font-body text-b4 leading-b4 text-neutral-700'>
            This section is temporarily unavailable. Please try refreshing the
            page.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
